import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import Loading from '../components/Loading';
import { useI18n } from '../i18n/index.jsx';

export default function AdminRequests() {
  const { isAdmin, setPage, isSuperuser } = useApp();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState('');

  const [paymentsPending, setPaymentsPending] = useState([]);
  const [paymentsUnpaid, setPaymentsUnpaid] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [coopContracts, setCoopContracts] = useState([]);
  const [workContracts, setWorkContracts] = useState([]);
  const [gatewayContracts, setGatewayContracts] = useState([]);
  const [partnerContracts, setPartnerContracts] = useState([]);
  const [companyRequests, setCompanyRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [workPermits, setWorkPermits] = useState([]);
  const [cryptoDeposits, setCryptoDeposits] = useState([]);
  const [invoicesPending, setInvoicesPending] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [openId, setOpenId] = useState('pay_pending');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin]);

  async function load() {
    setLoading(true);
    setError(null);

    const safeGet = async (url) => {
      try { return await api.get(url); } catch (e) { return { __err: e, __url: url }; }
    };

    const normalizeList = (res) => {
      if (!res || res.__err) return [];
      const d = res.data;
      if (Array.isArray(d)) return d;
      if (Array.isArray(d?.items)) return d.items;
      if (Array.isArray(d?.invoices)) return d.invoices;
      return [];
    };

    const [
      gwPendingRes,
      gwUnpaidRes,
      withdrawalsRes,
      coopContractsRes,
      workContractsRes,
      gatewayContractsRes,
      partnerContractsRes,
      companyReqRes,
      appsRes,
      cryptoRes,
      invoicesAllRes,
      workPermitsRes,
      invoicesAllAltRes,
    ] = await Promise.all([
      safeGet('/gateway-payments/admin/list?status=pending_verification'),
      safeGet('/gateway-payments/admin/list?status=unpaid&payment_type=activation'),
      safeGet('/admin/withdrawals'),
      safeGet('/admin/contracts'),
      safeGet('/work-contract/admin/list'),
      safeGet('/gateway/admin/list'),
      safeGet('/partner-contracts/admin/list?status=submitted'),
      safeGet('/admin/company-requests'),
      safeGet('/admin/applications?status=submitted'),
      safeGet('/crypto/deposits/pending'),
      safeGet('/admin/invoices/out'),
      safeGet('/admin/work-permits'),
      safeGet('/invoices/out'),
    ]);

    setPaymentsPending(gwPendingRes?.data?.payments || []);
    setPaymentsUnpaid(gwUnpaidRes?.data?.payments || []);

    const w = withdrawalsRes?.data || [];
    setWithdrawals(w.filter(x => x.status === 'requested'));

    const cc = coopContractsRes?.data || [];
    setCoopContracts(cc.filter(x => x.status === 'signed'));

    const wc = workContractsRes?.data || [];
    setWorkContracts(wc.filter(x => x.status === 'signed'));

    const gc = gatewayContractsRes?.data || [];
    setGatewayContracts(gc.filter(x => x.status === 'signed' || x.status === 'pending_payment'));

    const pc = partnerContractsRes?.data || [];
    setPartnerContracts(pc.filter(x => x.status === 'submitted'));

    const cr = companyReqRes?.data || [];
    setCompanyRequests(cr.filter(x => x.status === 'pending'));

    const ap = appsRes?.data || [];
    setApplications(ap.filter(x => x.status === 'submitted'));

    setCryptoDeposits(cryptoRes?.data || []);

    const invAll = normalizeList(invoicesAllRes).length > 0
      ? normalizeList(invoicesAllRes)
      : normalizeList(invoicesAllAltRes);

    const invPending = (invAll || []).filter(i => i.status === 'pending_verification');
    const profs = (invAll || []).filter(i => i.status === 'proforma');

    setInvoicesPending(invPending);
    setProformas(profs);
    setWorkPermits(workPermitsRes?.data || []);

    // If everything failed
    const responses = [gwPendingRes, gwUnpaidRes, withdrawalsRes, coopContractsRes, workContractsRes, gatewayContractsRes, partnerContractsRes, companyReqRes, appsRes, cryptoRes, invoicesAllRes, workPermitsRes, invoicesAllAltRes];
    const allFailed = responses.every(r => r && r.__err);
    if (allFailed) setError('Сеть недоступна. Проверьте подключение к интернету.');

    setLoading(false);
  }

  async function verifyInvoice(id, status) {
    try {
      await api.post(`/admin/invoices/out/${id}/verify?status=${status}`);
      setMsg(status === 'verified' ? '✅ Фактура подтверждена' : '❌ Фактура отклонена');
      load();
    } catch (e) {
      setMsg(e.userMessage || e.response?.data?.detail || 'Ошибка');
    }
  }

  async function deleteInvoice(id) {
    if (!confirm('Удалить фактуру?')) return;
    try {
      await api.delete(`/admin/invoices/out/${id}`);
      setMsg('🗑 Фактура удалена');
      load();
    } catch (e) {
      setMsg(e.userMessage || e.response?.data?.detail || 'Ошибка');
    }
  }

  async function openPdf(id) {
    setPreviewLoading(true);
    try {
      const res = await api.get(`/invoices/out/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setDownloadUrl(url);
    } catch (e) {
      setMsg(e.userMessage || e.response?.data?.detail || 'Ошибка загрузки PDF');
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setDownloadUrl('');
  }

  if (!isAdmin) return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  if (loading) return <Loading />;

  const total = paymentsPending.length + paymentsUnpaid.length + withdrawals.length + coopContracts.length + workContracts.length + gatewayContracts.length + partnerContracts.length + companyRequests.length + applications.length + workPermits.length + cryptoDeposits.length + invoicesPending.length + proformas.length;

  const sections = [
    {
      id: 'pay_pending',
      title: `💳 Платежи (ожидают проверки)`
        , count: paymentsPending.length,
      open: () => setPage('admin_gateway_payments'),
      empty: 'Нет платежей на проверке',
      items: paymentsPending.map(p => (
        <Item key={`pay-p-${p.id}`} title={`#${p.id} • ${p.user_name || 'Пользователь'}`} sub={`TG: ${p.telegram_id || '—'} • ${p.amount} ${p.currency} • ${p.payment_type}`} status={p.status} highlight />
      )),
    },
    {
      id: 'pay_unpaid',
      title: `💰 Платежи (новые, не оплачены)`
        , count: paymentsUnpaid.length,
      open: () => setPage('admin_gateway_payments'),
      empty: 'Нет новых платежей',
      items: paymentsUnpaid.map(p => (
        <Item key={`pay-u-${p.id}`} title={`#${p.id} • ${p.user_name || 'Пользователь'}`} sub={`TG: ${p.telegram_id || '—'} • ${p.amount} ${p.currency} • ${p.payment_type}`} status={p.status} />
      )),
    },
    {
      id: 'withdrawals',
      title: `💸 Выводы`,
      count: withdrawals.length,
      open: () => setPage('admin_payouts'),
      empty: 'Нет заявок на вывод',
      items: withdrawals.map(w => (
        <Item key={`wd-${w.id}`} title={`#${w.id} • Клиент ${w.client_id}`} sub={`Сумма: ${w.amount} PLN • Комиссия: ${w.commission_amount} PLN`} status={w.status} />
      )),
    },
    {
      id: 'contracts_coop',
      title: `✍️ Договоры (cooperation)`
        , count: coopContracts.length,
      open: () => setPage('admin_contracts'),
      empty: 'Нет новых договоров',
      items: coopContracts.map(c => (
        <Item key={`cc-${c.id}`} title={`#${c.id} • Клиент ${c.client_id}`} sub={`Фирма: ${c.company_id} • ${c.signed_at?.split('T')[0] || '—'}`} status={c.status} />
      )),
    },
    {
      id: 'contracts_work',
      title: `📝 Договоры (work)`
        , count: workContracts.length,
      open: () => setPage('admin_contracts'),
      empty: 'Нет новых work-договоров',
      items: workContracts.map(c => (
        <Item key={`wc-${c.id}`} title={`#${c.id} • ${c.user?.first_name || ''} ${c.user?.last_name || ''}`} sub={`Фирма: ${c.company?.name || c.company_id} • ${c.contract_type}`} status={c.status} />
      )),
    },
    {
      id: 'contracts_gateway',
      title: `🚀 Gateway договоры`
        , count: gatewayContracts.length,
      open: () => setPage('admin_contracts'),
      empty: 'Нет новых gateway-договоров',
      items: gatewayContracts.map(c => (
        <Item key={`gc-${c.id}`} title={`#${c.id} • ${c.user?.first_name || ''} ${c.user?.last_name || ''}`} sub={`Фирма: ${c.company?.name || c.company_id} • ${c.status}`} status={c.status} />
      )),
    },
    {
      id: 'contracts_partner',
      title: `📑 Договоры с контрагентами`,
      count: partnerContracts.length,
      open: () => setPage('admin_partner_contracts'),
      empty: 'Нет новых договоров',
      items: partnerContracts.map(c => (
        <Item key={`pc-${c.id}`} title={`#${c.id} • ${c.contract_type}`} sub={`Контрагент: ${c.counterparty_id} • ${c.created_at?.split('T')[0] || '—'}`} status={c.status} />
      )),
    },
    {
      id: 'company_requests',
      title: `🏢 Запросы на фирму`
        , count: companyRequests.length,
      open: () => setPage('admin_clients'),
      empty: 'Нет запросов',
      items: companyRequests.map(r => (
        <Item key={`cr-${r.id}`} title={`#${r.id} • ${r.user_name || 'Пользователь'}`} sub={`Фирма: ${r.company_name || r.company_id} • ${r.contract_type}`} status={r.status} />
      )),
    },
    {
      id: 'applications',
      title: `📑 Заявки (документы)`
        , count: applications.length,
      open: () => setPage('admin_applications'),
      empty: 'Нет заявок',
      items: applications.map(a => (
        <Item key={`app-${a.id}`} title={`#${a.id} • ${a.app_type}`} sub={`Клиент: ${a.client_id} • ${a.created_at?.split('T')[0] || '—'}`} status={a.status} />
      )),
    },
    {
      id: 'work_permits',
      title: `📋 Разрешения на работу`,
      count: workPermits.length,
      open: () => setPage('admin_work_permits'),
      empty: 'Нет заявок на разрешения',
      items: workPermits.map(w => (
        <Item key={`wp-${w.id}`} title={`#${w.id} • ${w.worker_name || 'Заявка'}`} sub={`Тип: ${w.permit_type_name || w.permit_type} • Статус: ${w.status}`} status={w.status} />
      )),
    },
    {
      id: 'invoices_pending',
      title: `🧾 Фактуры (на верификации)`
        , count: invoicesPending.length,
      open: () => setPage('admin_invoices'),
      empty: 'Нет фактур на проверке',
      items: invoicesPending.map(inv => (
        <div key={`inv-${inv.id}`} className="list-item" style={{ alignItems: 'center' }}>
          <div className="list-main">
            <div className="list-title">#{inv.id} • {inv.invoice_number}</div>
            <div className="list-sub">{inv.user_name || '—'} • TG: {inv.telegram_id || '—'}</div>
            <div className="list-sub">{inv.buyer_name}</div>
          </div>
          <div className="list-right">
            <div className={`status-badge status-${inv.status}`}>{inv.status}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button className="btn btn-tiny" onClick={() => verifyInvoice(inv.id, 'verified')}>✅</button>
              <button className="btn btn-tiny" onClick={() => verifyInvoice(inv.id, 'rejected')}>❌</button>
              {isSuperuser && (
                <button className="btn btn-tiny" onClick={() => deleteInvoice(inv.id)}>🗑</button>
              )}
              <button className="btn btn-tiny" onClick={() => openPdf(inv.id)} disabled={previewLoading} style={{ background: '#fff', color: '#111' }}>PDF</button>
            </div>
          </div>
        </div>
      )),
    },
    {
      id: 'proformas',
      title: `📄 Proforma`
        , count: proformas.length,
      open: () => setPage('admin_invoices'),
      empty: 'Нет проформ',
      items: proformas.map(inv => (
        <Item key={`pro-${inv.id}`} title={`#${inv.id} • ${inv.invoice_number}`} sub={`${inv.user_name || '—'} • ${inv.buyer_name}`} status={inv.status} />
      )),
    },
    {
      id: 'crypto',
      title: `🪙 Пополнения USDT`
        , count: cryptoDeposits.length,
      open: () => setPage('top_up'),
      empty: 'Нет заявок на пополнение',
      items: cryptoDeposits.map(d => (
        <Item key={`cd-${d.id}`} title={`#${d.id} • ${d.user_name || 'Пользователь'}`} sub={d.details || ''} status={'pending'} />
      )),
    },
  ];

  return (
    <div className="page"><BackButton />
      <h1>📌 Все заявки ({total})</h1>
      {error && <div className="message">❌ {error}</div>}
      {msg && <div className="message">{msg}</div>}

      <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b' }}>
        <b>🔥 Pending verification: {paymentsPending.length}</b>
      </div>

      {sections.map(sec => (
        <Accordion
          key={sec.id}
          title={`${sec.title} — ${sec.count}`}
          count={sec.count}
          open={openId === sec.id}
          onToggle={() => setOpenId(openId === sec.id ? null : sec.id)}
          onOpenPage={sec.open}
          emptyText={sec.empty}
        >
          {sec.items}
        </Accordion>
      ))}

      {previewUrl && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 900, height: '80vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Просмотр фактуры</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {downloadUrl && (
                  <a className="btn btn-secondary" href={downloadUrl} download={`invoice_${Date.now()}.pdf`}>Скачать PDF</a>
                )}
                <button className="btn btn-secondary" onClick={closePreview}>{t('common.close')}</button>
              </div>
            </div>
            <iframe title="invoice-preview" src={previewUrl} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }} />
          </div>
        </div>
      )}
    </div>
  );
}

function Accordion({ title, count, open, onToggle, onOpenPage, emptyText, children }) {
  const hasItems = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <div style={{ marginTop: 12, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
        <button className="btn btn-secondary btn-small" onClick={onToggle}>
          {open ? '▾' : '▸'} {title}
        </button>
        {onOpenPage && <button className="btn btn-secondary btn-small" onClick={onOpenPage}>Открыть</button>}
      </div>
      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          {hasItems ? <div className="list">{children}</div> : <div className="empty">{emptyText}</div>}
        </div>
      )}
    </div>
  );
}

function Item({ title, sub, status, highlight }) {
  return (
    <div className="list-item" style={highlight ? { border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)' } : undefined}>
      <div className="list-main">
        <div className="list-title">{title}</div>
        <div className="list-sub">{sub}</div>
      </div>
      <div className="list-right">
        <div className={`status-badge status-${status}`}>{status}</div>
      </div>
    </div>
  );
}
